'use client';

import { useEffect, useRef } from 'react';
import { useTheme } from '@loci/ui';

import { serializeThemeCookie, type SsrThemeSource, type ThemeMode } from '@/lib/theme/ssrTheme';
import { readThemePreferenceCookie } from '@/lib/theme/themePreference';

interface ThemeCookieSyncProps {
  source: SsrThemeSource;
  /** Parent-resolved mode (SSR cookie / default / forced). */
  committedMode: ThemeMode;
  onResolved: (mode: ThemeMode) => void;
}

/**
 * WEB-693 — persist the committed theme to `loci-theme` and lift user
 * toggles into the parent so a later Providers re-render cannot clobber
 * them with a stale SSR `mode` prop.
 *
 * Forced surfaces do not write the cookie so a city/admin visit cannot
 * overwrite the visitor's app preference. A stored `system` preference
 * (WEB-694) is also left alone — WEB-693's SSR parser treats it as
 * "no resolved cookie" (`source=default`), and rewriting the resolved
 * light/dark value would destroy System across a reload.
 *
 * ThemeProvider's `currentMode` lags the parent by one effect when the
 * server payload changes (forced → cookie). We must persist `committedMode`
 * in that window — publishing `useTheme().mode` would rewrite a light
 * cookie to dark and chase the parent (`light` ↔ `dark`).
 */
export function ThemeCookieSync({ source, committedMode, onResolved }: ThemeCookieSyncProps): null {
  const { mode: providerMode } = useTheme();
  const prevCommittedRef = useRef(committedMode);
  const prevProviderRef = useRef(providerMode);

  useEffect(() => {
    if (source === 'forced') {
      prevCommittedRef.current = committedMode;
      prevProviderRef.current = providerMode;
      return;
    }

    const committedChanged = prevCommittedRef.current !== committedMode;
    const providerChanged = prevProviderRef.current !== providerMode;
    prevCommittedRef.current = committedMode;
    prevProviderRef.current = providerMode;

    const storedPreference = readThemePreferenceCookie();
    if (providerChanged && !committedChanged && providerMode !== committedMode) {
      onResolved(providerMode);
      if (storedPreference !== 'system') {
        document.cookie = serializeThemeCookie(providerMode);
        document.documentElement.setAttribute('data-theme-source', 'cookie');
      }
      return;
    }

    // Echo the SSR-resolved light/dark cookie. Skip `source=default`
    // (missing cookie or `system`) so ThemePreferenceSync can own the
    // first write and keep System as a real stored value.
    if (source === 'cookie' && storedPreference !== 'system') {
      document.cookie = serializeThemeCookie(committedMode);
      document.documentElement.setAttribute('data-theme-source', 'cookie');
    }
  }, [committedMode, onResolved, providerMode, source]);

  return null;
}
